import { Controller } from "@hotwired/stimulus";
import { useStore } from "stimulus-store";
import { filteredItemsStore } from "../../stores/items/filtered_items_store";
import { allDevicesStore } from "../../stores/devices/all_devices_store";
import { devicesLoadingStore } from "../../stores/devices/devices_loading_store";
import { regionStore } from "../../stores/locales/region_store";
import { buildingStore } from "../../stores/locales/building_store";
import { areaStore } from "../../stores/locales/area_store";
import { roomStore } from "../../stores/locales/room_store";
import { isDevice } from "shared/device_types/device_type_id_helpers";

// Connects to data-controller="table--bundle-instance-table"
export default class extends Controller {

    static stores = [
        filteredItemsStore,
        allDevicesStore,
        devicesLoadingStore,
        regionStore,
        buildingStore,
        areaStore,
        roomStore
    ]

    static targets = [
        "itemsTable"
    ]

    connect() {
        useStore(this)
        
        // Clear all stores when bundle instance table controller initializes
        this.clearStoresForBundleInstances()
    }

    /**
     * Clear all stores to prevent conflicts when loading bundle instances
     */
    clearStoresForBundleInstances() {
        this.setAllDevicesStoreValue([])
        this.setDevicesLoadingStoreValue(false)
        this.setFilteredItemsStoreValue([])
        
        this.setRegionStoreValue({})
        this.setBuildingStoreValue({})
        this.setAreaStoreValue({})
        this.setRoomStoreValue({})
    }

    onFilteredItemsStoreUpdate() {
        if (!this.hasItemsTableTarget) {
            return
        }

        const items = this.filteredItemsStoreValue || []
        const aggregatedBundleInstances = this.aggregateItemsByBundleInstance(items);

        // Use argus--table if available
        Promise.resolve().then(() => {
            const argusTable = this._getArgusTable()
            if (argusTable) {
                argusTable.loadData(aggregatedBundleInstances)
            }
        })
    }

    /**
     * Get the argus--table controller instance from the items table target
     */
    _getArgusTable() {
        if (!this.hasItemsTableTarget) return null
        return this.application.getControllerForElementAndIdentifier(
            this.itemsTableTarget, 'argus--table'
        )
    }

    /**
     * Aggregate individual items by bundle_instance_id
     */
    aggregateItemsByBundleInstance(items) {
        const bundleItems = items.filter(item =>
            item.bundle_instance_id && item.bundle_instance_id.trim() !== ''
        )

        const bundleInstanceGroups = {}

        bundleItems.forEach(item => {
            const bundleInstanceId = item.bundle_instance_id

            if (!bundleInstanceGroups[bundleInstanceId]) {
                bundleInstanceGroups[bundleInstanceId] = []
            }

            bundleInstanceGroups[bundleInstanceId].push(item)
        })

        const bundleInstances = []

        Object.entries(bundleInstanceGroups).forEach(([bundleInstanceId, groupItems]) => {
            if (!groupItems || groupItems.length === 0) {
                console.warn(`Bundle instance ${bundleInstanceId} has no items, skipping`);
                return;
            }

            const deviceItem = groupItems.find(item => isDevice(item))
            const representativeItem = deviceItem || groupItems[0]

            if (!representativeItem) {
                console.warn(`Bundle instance ${bundleInstanceId} has no valid representative item, skipping`);
                return;
            }

            const bundleInstance = {
                id: bundleInstanceId,
                bundle_instance_id: bundleInstanceId,
                name: representativeItem.name,
                bundle: representativeItem.bundle,
                bundle_id: representativeItem.bundle?.id || representativeItem.bundle_id,
                locale: representativeItem.locale,
                deployment_status: representativeItem.deployment_status,
                item_count: groupItems.length,
                items: groupItems,
                deviceName: representativeItem.name,
                locationPath: this.buildLocationPath(representativeItem)
            }

            bundleInstances.push(bundleInstance)
        })

        return bundleInstances
    }

    /**
     * Build location path string
     */
    buildLocationPath(item) {
        const parts = []
        if (item.locale?.region?.name) {
            parts.push(item.locale.region.name)
        }
        if (item.locale?.building?.name) {
            parts.push(item.locale.building.name)
        }
        if (item.locale?.area?.name) {
            parts.push(item.locale.area.name)
        }
        if (item.locale?.room?.name) {
            parts.push(item.locale.room.name)
        }
        return parts.join(' > ')
    }

    /**
     * Get aggregated bundle instances data
     */
    getAggregatedData() {
        const items = this.filteredItemsStoreValue || []
        return this.aggregateItemsByBundleInstance(items)
    }
}
